/**
 * `<policy-template-picker>` — the template value builder (`SC-F513`, `SC-S513`).
 *
 * **It is a picker, not a text box.** Two upstream rules make that the only
 * defensible design:
 *
 *  1. A template must span the **whole** string. `"t-{{ subject.sub }}"` is a
 *     compile error, so there is no prefix or suffix field to offer.
 *  2. A variable that fails to render at query time **denies the request**. It
 *     never degrades to unfiltered rows — correct security posture, and a severe
 *     authoring footgun, because the failure arrives as an outage rather than as a
 *     warning.
 *
 * So the options are generated from the selected `SubjectBinding` and are restricted
 * to paths that will actually resolve for that binding's mode. `request.*` is
 * **absent entirely**: `request.remote_ip`, `request.user_agent` and
 * `request.headers.<name>` are dead on the live path (`SLUICE-U12`) and, uniquely,
 * a policy using one **passes** `sluice policy test` and then denies every query it
 * matches in production. A picker that offered them would be handing out a
 * CI-green outage.
 *
 * Two more things this element says out loud, because nothing else in the product
 * would. On a JWT binding the claim mapping cannot rename a claim for template
 * purposes, so the offered path is the claim as the issuer emits it. And when more
 * than one binding exists, the policy may be evaluated for a subject authenticated
 * by a *different* binding, against which the same template may be unresolvable —
 * so the warning names the others.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "../../components/index.js";
import type { BnSelectChangeDetail, BnSelectOption } from "../../components/index.js";
import {
  bindingMode,
  modeLabel,
  pathsFor,
  unwrap,
  wrap,
  type BindingSummary,
} from "./bindings.js";

/** Detail of the picker's `template-change` event. */
export interface TemplatePickerChangeDetail {
  /** The whole-string template, braces included, ready to store. */
  value: string;
  /** The binding the template was built against. Held by the editor so the
   * cross-binding warning survives a reload; it is not part of the policy. */
  binding: string;
}

@customElement("policy-template-picker")
export class PolicyTemplatePicker extends LitElement {
  /** The instance's readable bindings. */
  @property({ attribute: false }) bindings: BindingSummary[] = [];
  /** The binding the author is building against. */
  @property() binding = "";
  /** The current template string, braces included. */
  @property() value = "";
  /** How many bindings exist in total, so a partial load can say so. */
  @property({ type: Number }) bindingTotal = 0;
  /** The `claimPaths` group help from the form schema — the closed-vocabulary and
   * mode-asymmetry statement, rendered rather than paraphrased. */
  @property() claimsHelp = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .grid {
      display: grid;
      gap: var(--bino-space-sm);
    }
    bn-field {
      margin-bottom: 0;
    }
    .emits {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .emits code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius-sm);
      padding: 0 var(--bino-space-xs);
      overflow-wrap: anywhere;
    }
    p {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    p.deny {
      color: var(--bino-warning-text);
      background: var(--bino-warning-bg);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-sm);
    }
    code {
      font-family: var(--bino-font-mono);
    }
    .custom {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
    }
    .custom bn-input {
      flex: 1 1 auto;
      min-width: 0;
    }
    .prefix {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      padding-top: 0.5rem;
      color: var(--bino-text-secondary);
      white-space: nowrap;
    }
  `;

  private get selected(): BindingSummary | undefined {
    return this.bindings.find((b) => b.name === this.binding) ?? this.bindings[0];
  }

  /** The path currently inside the braces, or "" when the value is not a
   * whole-string reference. */
  private get path(): string {
    return unwrap(this.value);
  }

  /** True when the picker's own list does not contain the current path — an
   * imported template, or one typed against a claim the picker cannot enumerate. */
  private get isCustom(): boolean {
    const path = this.path;
    if (path === "") return false;
    return !pathsFor(this.selected).some((p) => p.path === path);
  }

  private emit(path: string, binding = this.selected?.name ?? "") {
    this.dispatchEvent(
      new CustomEvent<TemplatePickerChangeDetail>("template-change", {
        detail: { value: path === "" ? "" : wrap(path), binding },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private onBinding(e: CustomEvent<BnSelectChangeDetail>) {
    // bn-select's `change` is composed and would otherwise surface on this
    // element's own host as if the picker had fired it.
    e.stopPropagation();
    // Switching the binding re-derives the offer set, and a path that no longer
    // resolves must not survive the switch: keeping it would be exactly the silent
    // deny this element exists to prevent. It is cleared, and the author re-picks.
    const next = e.detail.value;
    const nextBinding = this.bindings.find((b) => b.name === next);
    const stillValid = pathsFor(nextBinding).some((p) => p.path === this.path);
    this.emit(stillValid ? this.path : "", next);
  }

  private renderBindingPicker() {
    const options: BnSelectOption[] = this.bindings.map((b) => ({
      value: b.name,
      label: b.name,
      description: modeLabel(b),
    }));
    return html`<bn-field
      label="Build against binding"
      hint="The paths below are the ones that resolve for this binding's credential mode. Switching the binding changes the list."
    >
      <bn-select
        .options=${options}
        .value=${this.selected?.name ?? ""}
        placeholder="Select a SubjectBinding…"
        @change=${this.onBinding}
      ></bn-select>
    </bn-field>`;
  }

  private renderPathPicker() {
    const paths = pathsFor(this.selected);
    const options: BnSelectOption[] = paths.map((p) => ({
      value: p.path,
      label: p.label,
      description: p.list ? `${p.help} (a list)` : p.help,
    }));
    const current = this.isCustom ? "" : this.path;
    return html`<bn-field
      label="Subject attribute"
      hint="Only paths that resolve for the selected binding are offered. request.remote_ip, request.user_agent and request.headers are absent: they are empty on the live path, and an unresolvable template denies the query."
    >
      <bn-select
        filterable
        .options=${options}
        .value=${current}
        placeholder="Select an attribute…"
        @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
          e.stopPropagation();
          this.emit(e.detail.value);
        }}
      ></bn-select>
    </bn-field>`;
  }

  /** The escape hatch for a claim the picker cannot enumerate — the issuer's own
   * token claims are not knowable from the binding. It is still not a free text
   * field: the root is fixed at `subject.claims.` and the braces are added, so the
   * whole-string rule cannot be broken. */
  private renderClaimPath() {
    const binding = this.selected;
    if (!binding) return nothing;
    const mode = bindingMode(binding);
    if (mode !== "jwt" && mode !== "both") return nothing;
    const path = this.path;
    const custom = path.startsWith("subject.claims.") ? path.slice("subject.claims.".length) : "";
    return html`<bn-field
      label="Or another claim from the token"
      hint="The issuer's own claim names cannot be listed from the binding. On the JWT path the claim mapping does not rename a claim for template purposes, so name it exactly as the token carries it."
    >
      <div class="custom">
        <span class="prefix" aria-hidden="true">subject.claims.</span>
        <bn-input
          mono
          aria-label="Claim path under subject.claims"
          placeholder="tenant_id"
          .value=${custom}
          @input=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.emit(e.detail.value === "" ? "" : `subject.claims.${e.detail.value}`);
          }}
        ></bn-input>
      </div>
    </bn-field>`;
  }

  /** The warning `03` §3.4 asks for, naming the other bindings. */
  private renderCrossBinding() {
    const others = this.bindings.filter((b) => b.name !== this.selected?.name);
    if (others.length === 0) return nothing;
    return html`<p>
      This policy is not scoped to one binding. A subject authenticated by
      ${others.map((b, i) => html`${i > 0 ? ", " : ""}<code>${b.name}</code>`)} may also be matched,
      and this template may not resolve against ${others.length === 1 ? "it" : "them"} — in which case
      every query it matches is denied. Narrow the policy's subject matcher, or pick a path that
      resolves for every binding that can reach the matched tables.
    </p>`;
  }

  override render() {
    if (this.bindings.length === 0) {
      return html`<bn-panel eyebrow="No binding to build against">
        <p>
          A template value resolves against an authenticated subject, and this instance has no
          readable <code>SubjectBinding</code>${this.bindingTotal > 0
            ? html` — ${this.bindingTotal} exist${this.bindingTotal === 1 ? "s" : ""} and could not be
              read`
            : nothing}. The paths that resolve differ between a JWT binding and an API-key one, so
          offering a list here would be a guess, and a template that fails to render denies the query
          rather than returning unfiltered rows. Create a binding first.
        </p>
      </bn-panel>`;
    }
    return html`
      <div class="grid">
        ${this.renderBindingPicker()} ${this.renderPathPicker()} ${this.renderClaimPath()}
        <div class="emits">
          <span>Emits</span>
          ${this.value
            ? html`<code>${this.value}</code>`
            : html`<span>nothing yet — an empty template value denies every query this policy
                matches.</span>`}
        </div>
        <p class="deny" role="note">
          A template that fails to render at query time <strong>denies the request</strong>. It never
          degrades to unfiltered rows, so a path that does not exist for the subject's binding is an
          outage and not a warning.
        </p>
        ${this.claimsHelp ? html`<p>${this.claimsHelp}</p>` : nothing} ${this.renderCrossBinding()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-template-picker": PolicyTemplatePicker;
  }
}
